import axios from 'axios';

// Canonical list of the "EMR upload status" filter options. This used to be copy-pasted
// into InboxFilterBar, ReferralFilterBar and the legacy AutoUploadFilters sidebar; all
// three now build from here so an option can never exist in one bar and not another.
export const AUTO_UPLOAD_FILTER_OPTIONS = [
  { label: 'Failed', value: 'failedAutoUploaded' },
  { label: 'Successful', value: 'successfulAutoUploaded' },
  { label: 'Send to Chart', value: 'autoUploadSendToChart' },
  { label: 'Create New Chart + Task', value: 'autoUploadChartCreation' },
  { label: 'Create Task', value: 'autoUploadTask' },
  { label: 'Auto-Upload Not Attempted', value: 'notAttemptedAutoUploaded', className: 'auto-upload-filter-divider' },
];

export const AUTO_UPLOAD_FILTER_KEYS = AUTO_UPLOAD_FILTER_OPTIONS.map((option) => option.value);
export const NOT_ATTEMPTED_AUTO_UPLOAD_FILTER_KEY = 'notAttemptedAutoUploaded';

// This filter represents records with no upload attempt, so it cannot be combined
// with a status or action filter that requires an upload attempt.
export const normalizeAutoUploadFilterValues = (values, previousValues = []) => {
  const hasNotAttempted = values.includes(NOT_ATTEMPTED_AUTO_UPLOAD_FILTER_KEY);
  const previouslyHadNotAttempted = previousValues.includes(NOT_ATTEMPTED_AUTO_UPLOAD_FILTER_KEY);

  if (hasNotAttempted && !previouslyHadNotAttempted) return [NOT_ATTEMPTED_AUTO_UPLOAD_FILTER_KEY];
  if (previouslyHadNotAttempted) return values.filter((value) => value !== NOT_ATTEMPTED_AUTO_UPLOAD_FILTER_KEY);
  return values;
};

/**
 * Fetches which upload-type filters this org has any history for.
 *
 * @returns {Promise<Object|null>} { send_to_chart, chart_creation, task }, or null when
 *   the availability is unknown (request failed). Null is the fail-open signal —
 *   buildAutoUploadFilterOptions shows every option rather than hiding a filter because
 *   a request errored.
 */
export const fetchAutoUploadFilterAvailability = async () => {
  try {
    const response = await axios.get('/api/v1/emr_upload_filters');
    const availability = response?.data?.availability;
    return availability && typeof availability === 'object' ? availability : null;
  } catch (error) {
    console.error('Failed to fetch EMR upload filter availability', error);
    return null;
  }
};

// Availability is per-org and effectively static, so the three filter bars share one
// in-flight request per page load instead of each firing their own.
let availabilityPromise = null;

export const loadAutoUploadFilterAvailability = () => {
  if (!availabilityPromise) {
    availabilityPromise = fetchAutoUploadFilterAvailability();
  }
  return availabilityPromise;
};

// Test seam — the module-level cache would otherwise leak between cases.
export const resetAutoUploadFilterAvailabilityCache = () => {
  availabilityPromise = null;
};

const hasHistory = (availability, key) => availability === null
  || availability === undefined
  || availability[key] === true;

/**
 * Narrows the option list to what this org can actually produce.
 *
 * An option survives when the org is licensed for the workflow today OR already has
 * uploads of that type (a downgraded org keeps the records, so it keeps the filter).
 * Failed/Successful/Send to Chart are status filters over every upload type, so they
 * ride the block-level `manual_upload_emr` gate the caller already applies.
 *
 * @param {Object}  params
 * @param {boolean} params.createPatientChart  `create_patient_chart` feature flag
 * @param {boolean} params.taskCreation        `task_creation` feature flag
 * @param {boolean} params.hideTaskButton      `hide_task_creation_button` feature flag
 * @param {Object}  params.availability        from loadAutoUploadFilterAvailability(); null = unknown
 * @param {Array}   params.selectedValues      currently applied filters, never hidden
 */
export const buildAutoUploadFilterOptions = ({
  createPatientChart = false,
  taskCreation = false,
  hideTaskButton = false,
  availability = null,
  selectedValues = [],
} = {}) => {
  const canCreateChart = createPatientChart || hasHistory(availability, 'chart_creation');
  const canCreateTask = (taskCreation && !hideTaskButton) || hasHistory(availability, 'task');

  return AUTO_UPLOAD_FILTER_OPTIONS.filter(({ value }) => {
    // A bookmarked or shared URL can carry a filter this org would no longer be
    // offered. Dropping the option strands it as a bare key in the antd tag, so an
    // applied filter always stays in the list until the user clears it.
    if (selectedValues.includes(value)) return true;

    if (value === 'autoUploadChartCreation') return canCreateChart;
    if (value === 'autoUploadTask') return canCreateTask;
    return true;
  });
};
